import { inject, Injectable } from '@angular/core';
import { DateService } from './date.service';
import { IconType } from '../components/generic-card/models/card-config.interface';

export type LineStatus = 'active' | 'waiting' | 'terminated';

@Injectable({
  providedIn: 'root',
})
export class StatusCalculationService {
  private dateService = inject(DateService);

  /**
   * Calculates line status based on dateJoined and dateLeft dates.
   * Same logic used in TPA and Person features.
   *
   * @param dateJoined - The date when coverage started or will start
   * @param dateLeft - The date when coverage ended or will end (optional)
   * @returns 'waiting' if coverage hasn't started, 'terminated' if ended, 'active' otherwise
   */
  calculateLineStatus(dateJoined: string, dateLeft: string): LineStatus {
    const today = this.dateService.formatToDateString(this.dateService.todayDate());
    const joinedDate = this.dateService.formatToDateString(dateJoined);
    const leftDate = dateLeft ? this.dateService.formatToDateString(dateLeft) : null;

    // Waiting: coverage hasn't started yet
    if (joinedDate > today) {
      return 'waiting';
    }

    // Terminated: coverage has ended
    if (leftDate && leftDate <= today) {
      return 'terminated';
    }

    // Active: currently active coverage
    return 'active';
  }

  /**
   * Converts lineStatus to IconType for status dot display.
   *
   * @param lineStatus - The calculated line status
   * @returns IconType: 'active' (green), 'warning' (orange), 'inactive' (red)
   */
  lineStatusToIconType(lineStatus: LineStatus): IconType {
    switch (lineStatus) {
      case 'active':
        return 'active'; // Green
      case 'waiting':
        return 'warning'; // Orange
      case 'terminated':
        return 'inactive'; // Red
      default:
        return 'inactive';
    }
  }
}
